import type { AdminSelf } from '@saay/api-client';
import { Combobox, IconButton, PHONE_QUERY, useMediaQuery } from '../kit.js';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';

import { buyerSearchFromText } from '../buyer-search.js';
import { canSeeArea } from '../permissions.js';
import { useBuyers, useMerchants } from '../queries.js';

const MERCHANT_MIN = 3;

function merchantOptionValue(id: string): string {
  return `merchant:${id}`;
}

function buyerOptionValue(id: string): string {
  return `buyer:${id}`;
}

/** ADM-02: shell search for merchants (name) and buyers (mobile) where the role may open them. */
export function GlobalHeaderSearch({ self }: { readonly self: AdminSelf | undefined }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  // On a phone the bar keeps the page title; the search opens from an icon over it (MD3 search).
  const phone = useMediaQuery(PHONE_QUERY, false);
  const [expanded, setExpanded] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (expanded) {
      boxRef.current?.querySelector('input')?.focus();
    }
  }, [expanded]);
  const trimmed = query.trim();
  const canMerchants = self !== undefined && canSeeArea(self, 'merchants');
  const canBuyers = self !== undefined && canSeeArea(self, 'buyers');
  const merchantReady = canMerchants && trimmed.length >= MERCHANT_MIN;
  const buyerParse = buyerSearchFromText(trimmed);
  const buyerReady =
    canBuyers && trimmed !== '' && !('message' in buyerParse && buyerParse.message !== undefined);
  const buyerFilters = buyerReady && 'filters' in buyerParse ? buyerParse.filters : {};
  const buyers = useBuyers(buyerFilters, buyerReady);
  const merchants = useMerchants({ q: trimmed }, merchantReady);
  const merchantRows = merchants.data?.pages.flatMap((page) => page.items) ?? [];
  const buyerRows = buyers.data?.pages.flatMap((page) => page.items) ?? [];
  const options = useMemo(() => {
    const items: { label: string; value: string }[] = [];
    if (merchantReady) {
      for (const row of merchantRows.slice(0, 8)) {
        items.push({
          label: `فروشگاه · ${row.displayNameFa}`,
          value: merchantOptionValue(row.id),
        });
      }
    }
    if (buyerReady) {
      for (const row of buyerRows.slice(0, 8)) {
        items.push({
          label: `خریدار · ${row.firstNameFa} ${row.lastNameFa}`,
          value: buyerOptionValue(row.buyerId),
        });
      }
    }
    return items;
  }, [buyerReady, buyerRows, merchantReady, merchantRows]);

  if (self === undefined || (!canMerchants && !canBuyers)) {
    return null;
  }

  const loading = (merchantReady && merchants.isFetching) || (buyerReady && buyers.isFetching);

  if (phone && !expanded) {
    return <IconButton icon="search" label="جست‌وجوی سراسری" onClick={() => setExpanded(true)} />;
  }

  return (
    <div
      className="admin-global-search saay-fields-compact"
      data-expanded={phone || undefined}
      ref={boxRef}
    >
      <Combobox
        // The server already searched (a mobile is not in «خریدار · نام»), so nothing is hidden.
        filterOptions={false}
        inputValue={query}
        label="جست‌وجوی سراسری"
        loading={loading}
        onChange={(value) => {
          if (value.startsWith('merchant:')) {
            void navigate(`/merchants/${value.slice('merchant:'.length)}`);
          } else if (value.startsWith('buyer:')) {
            void navigate(`/buyers/${value.slice('buyer:'.length)}`);
          }
          setQuery('');
          setExpanded(false);
        }}
        onInputValueChange={setQuery}
        options={options}
        placeholder="فروشگاه یا موبایل خریدار"
        value={undefined}
      />
      {phone ? (
        <IconButton
          icon="close"
          label="بستن جست‌وجو"
          onClick={() => {
            setQuery('');
            setExpanded(false);
          }}
        />
      ) : null}
    </div>
  );
}
